import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  ScrollArea,
  Skeleton,
  Text,
  toastManager,
} from "@salestrak/ui";

import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter, SettingsBackBar, SettingsBackBarClearance, } from "#app/components/page-header";
import { LoadErrorState } from "#app/components/settings/load-error-state";

import { type DangerZoneConfig } from "#app/components/settings/danger-zone";
import { ProfileForm } from "#app/components/settings/profile-form";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { MEMBER_STATUS_BADGE } from "#app/components/members/member-status-badge";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { withPermission } from "#app/components/permissions/require-permission";

type PendingAction = "suspend" | "reactivate" | "cancel-invite";

function MemberProfilePage() {
  const { userId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { currentUser } = useAppData();
  const { updateMember, resendInvite, cancelInvite } = useMemberMutations();
  const { user, loading, error } = useMemberDetail(userId);

  const canUpdateMembers = usePermission("manage.users");
  const canResendInvite = usePermission("manage.users");

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  // A member who left comes back through a fresh invite they accept, never
  // an admin flipping them active — so the door here is the invite dialog,
  // seeded with their address.
  const [reinviteOpen, setReinviteOpen] = useState(false);

  const handleFieldSave = useCallback(
    // `null` clears a nullable field (the job title); text fields save strings.
    async (fieldId: string, value: string | null) => {
      if (!userId) return;
      await updateMember(userId, { [fieldId]: value }, { autosave: true });
    },
    [userId, updateMember],
  );

  const memberLabel = user?.name?.trim() || user?.email || "";

  const handleConfirm = useCallback(async () => {
    if (!userId || !pendingAction) return;
    if (pendingAction === "cancel-invite") {
      const result = await cancelInvite(userId);
      if (!result) return;
      setPendingAction(null);
      replace("/settings/members");
      return;
    }
    const result = await updateMember(userId, {
      memberStatus: pendingAction === "suspend" ? "suspended" : "active",
    });
    if (result) {
      toastManager.add({
        title: pendingAction === "suspend" ? "Member suspended" : "Member reactivated",
        type: "success",
      });
    }
    setPendingAction(null);
  }, [userId, pendingAction, cancelInvite, updateMember, replace]);

  const handleResendInvite = useCallback(async () => {
    if (!userId) return;
    await resendInvite(userId);
  }, [userId, resendInvite]);

  const isCurrentUser = currentUser?.userId === userId;
  const memberStatus = user?.memberStatus;

  const dangerZone = useMemo<DangerZoneConfig[]>(() => {
    if (!user) return [];
    const actions: DangerZoneConfig[] = [];

    if (memberStatus === "invited" && canResendInvite) {
      actions.push({
        id: "resend-invite",
        label: "Resend the invitation email",
        buttonLabel: "Resend invite",
        variant: "default",
        onClick: handleResendInvite,
      });
      actions.push({
        id: "cancel-invite",
        label: "Cancel this invitation",
        buttonLabel: "Cancel invite...",
        variant: "destructive",
        onClick: () => setPendingAction("cancel-invite"),
      });
    }

    if (memberStatus === "active" && canUpdateMembers && !isCurrentUser) {
      actions.push({
        id: "suspend",
        label: "Remove from the workspace",
        subtitle: "They lose access right away. You can reactivate them later.",
        buttonLabel: "Suspend member...",
        variant: "destructive",
        onClick: () => setPendingAction("suspend"),
      });
    }

    if (memberStatus === "suspended" && canUpdateMembers) {
      actions.push({
        id: "reactivate",
        label: "Reactivate this member",
        buttonLabel: "Reactivate user...",
        variant: "default",
        onClick: () => setPendingAction("reactivate"),
      });
    }

    if (memberStatus === "left" && canUpdateMembers) {
      actions.push({
        id: "reinvite",
        label: "Invite this member back",
        subtitle: "They left the workspace. A new invite restores their access once they accept it.",
        buttonLabel: "Re-invite...",
        variant: "default",
        onClick: user.email ? () => setReinviteOpen(true) : undefined,
        disabledReason: user.email ? undefined : "This member has no email address to invite",
      });
    }

    return actions;
  }, [
    user,
    memberStatus,
    canResendInvite,
    canUpdateMembers,
    isCurrentUser,
    handleResendInvite,
  ]);

  if (loading && !user) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: back bar + header geometry hold through the resolve;
          * only the member name is a bar. Groups mirror the settled run —
          * the 6-row profile fields card and the danger zone. */}
        <SettingsBackBar label="Members" onClick={() => replace("/settings/members")} />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader centered pinnedBack>
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton groups={[{ rows: 6 }, { rows: 1 }]} />
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <LoadErrorState noun="member profile" error={error ?? null} />;
  }

  const dialogTitle =
    pendingAction === "suspend"
      ? `Suspend ${memberLabel}?`
      : pendingAction === "reactivate"
        ? `Reactivate ${memberLabel}?`
        : pendingAction === "cancel-invite"
          ? `Cancel invite for ${memberLabel}?`
          : "";

  const dialogDescription =
    pendingAction === "suspend"
      ? `${memberLabel} will no longer be able to access this workspace.`
      : pendingAction === "reactivate"
        ? `${memberLabel} will regain access to this workspace.`
        : pendingAction === "cancel-invite"
          ? `${memberLabel} will be removed from this workspace. The invitation link in their email will stop working.`
          : "";

  const dialogConfirmLabel =
    pendingAction === "suspend"
      ? "Suspend member"
      : pendingAction === "reactivate"
        ? "Reactivate member"
        : "Cancel invite";

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="Members" onClick={() => replace("/settings/members")} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle
            adornment={
              memberStatus && MEMBER_STATUS_BADGE[memberStatus] ? (
                <Box>
                  <Badge
                    color={MEMBER_STATUS_BADGE[memberStatus].color}
                    tone={MEMBER_STATUS_BADGE[memberStatus].tone}
                  >
                    <Text>{MEMBER_STATUS_BADGE[memberStatus].label}</Text>
                  </Badge>
                </Box>
              ) : undefined
            }
          >
            {user.name || user.email}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <ProfileForm
              user={user}
              onFieldSave={handleFieldSave}
              dangerZone={dangerZone}
            />

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>

      {reinviteOpen && user.email ? (
        <InviteMembersDialog
          open
          initialEmails={[user.email]}
          onOpenChange={(open) => {
            if (!open) setReinviteOpen(false);
          }}
        />
      ) : null}

      <AlertDialog
        open={!!pendingAction}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>{dialogDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button
              color={pendingAction === "reactivate" ? "primary" : "destructive"}
              onClick={handleConfirm}
            >
              {dialogConfirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/members/$userId",
)({ component: withPermission("manage.users", MemberProfilePage) });
